"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CSVFileImportPage = exports.CSVFileImportPageConfig = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const core_shared_1 = require("@ikegps/core-shared");
const mobx_1 = require("mobx");
const image_button_1 = require("../../components/button/image.button");
const file_upload_1 = require("../../components/file.upload/file.upload");
const icon_1 = require("../../components/icon");
const icons_1 = require("../../components/icon/icons");
const input_select_1 = require("../../components/input/input.select");
const tag_input_state_1 = require("../../components/input/tag.input.state");
const modal_service_1 = require("../../components/modal/modal.service");
const modal_style_1 = require("../../components/modal/modal.style");
const notification_service_1 = require("../../components/notification.toast/notification.service");
const spinner_1 = require("../../components/spinner/spinner");
const theme_1 = require("../../components/theme");
const i18n_1 = require("../../i18n");
const mithril_1 = require("../../mithril");
const export_notification_detail_ctrl_1 = require("../../modules/export.notification/export.notification.detail/export.notification.detail.ctrl");
const services_1 = require("../../services");
const store_1 = require("../../store");
const classnames_1 = require("../../util/classnames");
const page_1 = require("../page");
const import_ctrl_1 = require("./import.ctrl");
const import_style_1 = require("./import.style");
const MAX_COLLECTIONS = 500;
const COLLECTION_CHUNK = 25;
exports.CSVFileImportPageConfig = {
    type: 'internal',
    name: page_1.IkePageName.collectionImport,
    path: '/collection/import',
    auth: {
        required: true,
        tokens: [core_api_1.IkeToken.FEATURE_IMPORT_CSV],
        userLevel: [core_api_1.IkeUserRole.USER, core_api_1.IkeUserRole.ROOT, core_api_1.IkeUserRole.DATA_ADMIN],
    },
    nav: {
        name: i18n_1.T.LIST_COLLECTION_IMPORT,
        icon: icons_1.MaterialIcon.INBOX,
    },
};
class CSVFileImportPage extends page_1.IkePage {
    get canImportCSV() {
        return this.isCorrectUserType && services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_IMPORT_CSV);
    }
    get canImportExcel() {
        return this.isCorrectUserType && services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_IMPORT_EXCEL);
    }
    get canImport() {
        return this.canImportCSV || this.canImportExcel;
    }
    get isExcelImport() {
        return this.mode === 'EXCEL';
    }
    get isCSVImport() {
        return this.mode === 'CSV';
    }
    get isCorrectUserType() {
        const userProfile = services_1.Services.UserProfile.get(services_1.Services.User.current.objectId);
        return services_1.Services.UserProfile.canImportCollections(userProfile);
    }
    get isValid() {
        var _a, _b;
        if (this.tagName == null) {
            return false;
        }
        if (this.isExcelImport) {
            return ((_a = this.fileUpload.file) === null || _a === void 0 ? void 0 : _a.type) === file_upload_1.EXCEL_MIME_TYPE || ((_b = this.fileUpload.file) === null || _b === void 0 ? void 0 : _b.type) === file_upload_1.OPEN_EXCEL_MIME_TYPE;
        }
        if (this.isCSVImport) {
            if (!this.csvImport.isValid) {
                return false;
            }
            if (this.csvImport.collections.length > MAX_COLLECTIONS || this.csvImport.collections.length < 1) {
                return false;
            }
        }
        return true;
    }
    constructor() {
        super(exports.CSVFileImportPageConfig);
        this.form = new input_select_1.SelectInputState('Select a Form');
        this.fileUpload = new file_upload_1.FileUploadComponent();
        this.csvImport = new import_ctrl_1.CSVFormImportControl();
        this.isImporting = false;
        this.progressCurrent = 0;
        this.progressTotal = 0;
        this.mode = null; // Initial value is set in constructor
        this.tabs = [];
        this.activeTab = 0;
        this.tag = new tag_input_state_1.TagNameInputState();
        this.tag.state.setAutoFocus(true);
        this.reaction(() => this.mode, () => {
            if (this.mode === 'EXCEL') {
                this.fileUpload.uploadType = 'buffer';
            }
            if (this.mode === 'CSV') {
                this.fileUpload.uploadType = 'text';
            }
        });
        this.reaction(() => this.fileUpload.file && this.fileUpload.file.text, this.processCSVFile);
        this.reaction(() => this.fileUpload.file && this.fileUpload.file.isFailed, this.processFileFailure);
        this.reaction(() => this.form.value, this.processCSVFile);
        this.reaction(() => store_1.AppStore.FormSummary.all, this.processFormList);
        // Select initial mode (active tab) based on permissions
        if (this.canImportExcel) {
            this.mode = 'EXCEL';
            this.fileUpload.uploadType = 'buffer';
        }
        else if (this.canImportCSV) {
            this.mode = 'CSV';
            this.fileUpload.uploadType = 'text';
        }
        // If we're navigating back to this page, and the FormSummaries have already been loaded in the app store
        // the above reaction will never be triggered and the form list will never be populated. This
        // manually calls the processFormList method to ensure it's populated regardless.
        this.processFormList(store_1.AppStore.FormSummary.all);
        this.tabs = [
            ...(this.canImportExcel ? [{ title: (0, i18n_1.Translate)(i18n_1.T.EXCEL), onSelect: () => (this.mode = 'EXCEL') }] : []),
            ...(this.canImportCSV ? [{ title: (0, i18n_1.Translate)(i18n_1.T.CSV), onSelect: () => (this.mode = 'CSV') }] : []),
        ];
        this.isImporting = false;
    }
    get tagName() {
        const tagValue = this.tag.value;
        if (tagValue == null || tagValue.trim() === '') {
            return null;
        }
        return tagValue;
    }
    processCSVFile() {
        // Only run if CSV import is selected
        if (!this.isCSVImport) {
            return;
        }
        if (this.form.value == null) {
            this.csvImport.reset();
            return;
        }
        const file = this.fileUpload.file;
        if (file == null) {
            this.csvImport.reset();
            return;
        }
        const text = this.fileUpload.file.text;
        if (text == null) {
            this.csvImport.reset();
            return;
        }
        try {
            this.csvImport.process(this.form.value, new core_shared_1.IkeCSV(text));
        }
        catch (_e) {
            notification_service_1.NotificationService.error({ key: i18n_1.T.FILE_NOT_SUPPORTED });
            this.fileUpload.reset();
            this.csvImport.reset();
        }
    }
    setProgress(current, total) {
        this.progressCurrent = current;
        this.progressTotal = total;
    }
    processFileFailure(failed) {
        if (!failed) {
            return;
        }
        this.setImporting(false);
        this.fileUpload.reset();
        notification_service_1.NotificationService.error({ key: i18n_1.T.FILE_NOT_SUPPORTED });
    }
    setImporting(importing) {
        this.isImporting = importing;
        this.setProgress(0, 0);
    }
    importCSVCollections() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (this.isImporting) {
                return;
            }
            this.setProgress(0, this.csvImport.collections.length);
            this.isImporting = true;
            try {
                yield this.processCSVImport();
                this.setImporting(false);
            }
            catch (e) {
                notification_service_1.NotificationService.error({ key: i18n_1.T.COLLECTION_IMPORT_FAILED });
                services_1.Services.Log.Logger.error(e, 'Error importing collections');
            }
        });
    }
    importExcelCollections() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (this.isImporting) {
                return;
            }
            this.setImporting(true);
            // Upload the Excel file attachment
            const file = this.fileUpload.file;
            const rest = services_1.Services.Department.rest;
            const attachment = yield rest.Attachment.set(file.name, file.text, file.type);
            // Run import process on backend
            const response = yield rest.Collection.Import.excel({
                attachmentId: attachment.objectId,
                formRevision: this.form.value,
                tagName: this.tagName,
            });
            const importHasProblems = response.results.collections.some((n) => n.status === 'error') ||
                response.results.meta.length > 0 ||
                response.results.collections.length === 0;
            const redirectToJob = () => {
                var _a;
                modal_service_1.ModalService.close();
                services_1.Services.Route.go(page_1.IkePageName.map, {
                    t: response.jobID,
                    collection: (_a = response.results.collections[0].collection) === null || _a === void 0 ? void 0 : _a.uuid,
                });
            };
            const redirectButton = (0, mithril_1.m)('button', {
                className: `mdl-button ${modal_style_1.CSS.action.ok}`,
                onclick: redirectToJob,
                type: 'submit',
            }, (0, i18n_1.Translate)(i18n_1.T.OK));
            this.setImporting(false);
            services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Import, 'excel:collection', attachment.objectId, response.results.collections.length);
            if (response.results.collections.length > 0 || response.results.meta.length > 0) {
                // Display import notifications window
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                modal_service_1.ModalService.open({
                    title: 'Import Notifications',
                    content: renderImportResults(response.jobID, response.results),
                    actions: !importHasProblems ? redirectButton : null,
                });
                return;
            }
            else {
                notification_service_1.NotificationService.error({ key: i18n_1.T.IMPORT_FILE_NO_ROWS });
            }
        });
    }
    processFormList(summaries) {
        this.form.setOptions(summaries.map((c) => {
            return { value: c.revision, key: c.displayname };
        }));
    }
    processCSVImport() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const collections = this.csvImport.collections;
            const file = this.fileUpload.file;
            const rest = services_1.Services.Department.rest;
            const attachment = yield rest.Attachment.set(file.name, file.text, file.type);
            const mapping = this.csvImport.mapper.toJSON();
            const importArgs = {
                attachmentId: attachment.objectId,
                tagName: this.tag.value,
                formId: this.csvImport.form.id,
                mapping,
                count: COLLECTION_CHUNK,
                skip: 0,
            };
            for (const info of mapping) {
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Import, 'csv:mapping', info.fieldId + ':' + info.csvHeader, 1);
            }
            let response;
            for (let i = 0; i < collections.length; i += COLLECTION_CHUNK) {
                importArgs.skip = i;
                response = yield rest.Collection.Import.csv(importArgs);
                this.setProgress(i, collections.length);
            }
            notification_service_1.NotificationService.success({ key: i18n_1.T.COLLECTION_IMPORT_SUCCESS, attrs: { count: collections.length } });
            services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Import, 'csv:collection', attachment.objectId, response.results.collections.length);
            services_1.Services.Route.go(page_1.IkePageName.map, {
                t: response.results.jobId,
                collection: response.results.collections[0].uuid,
            });
            this.setProgress(null, null);
        });
    }
    getCsvNodes() {
        if (!this.canImportCSV) {
            return null;
        }
        const children = [];
        children.push(this.viewJobSelector());
        children.push(this.viewFormSelector());
        children.push(this.viewFileUpload());
        if (this.fileUpload.isFilePresent === false || this.form.value == null) {
            return children;
        }
        if (this.isImporting) {
            children.push((0, mithril_1.m)('div', { className: import_style_1.CSS.option.container + ' ' + import_style_1.CSS.option.importing }, [
                (0, mithril_1.m)('div', { className: import_style_1.CSS.option.textContainer }, [
                    spinner_1.Spinner.view(),
                    (0, mithril_1.m)('span', { className: import_style_1.CSS.option.text }, (0, i18n_1.Translate)(i18n_1.T.COLLECTION_IMPORT_PROGRESS, {
                        count: this.progressCurrent,
                        total: this.progressTotal,
                    })),
                ]),
            ]));
            return children;
        }
        children.push(this.viewFileMappings());
        children.push((0, mithril_1.m)('div', { className: import_style_1.CSS.importButton }, [
            (0, mithril_1.m)('button', {
                className: 'mdl-button mdl-button--raised mdl-button--accent',
                disabled: !this.isValid,
                onclick: this.importCSVCollections,
            }, [(0, i18n_1.Translate)(i18n_1.T.COLLECTION_IMPORT_GO)]),
        ]));
        return children;
    }
    getExcelNodes() {
        if (!this.canImportExcel) {
            return null;
        }
        const children = [];
        children.push(this.viewUserGuideLink());
        children.push(this.viewFormSelector());
        children.push(this.viewExcelTemplateDownload());
        children.push(this.viewJobSelector());
        children.push(this.viewFileUpload());
        children.push((0, mithril_1.m)('div', { className: import_style_1.CSS.importButton }, [
            (0, mithril_1.m)('button', {
                className: 'mdl-button mdl-button--raised mdl-button--accent',
                disabled: !this.isValid || this.isImporting,
                onclick: this.importExcelCollections,
            }, [this.isImporting ? spinner_1.Spinner.view() : (0, i18n_1.Translate)(i18n_1.T.COLLECTION_IMPORT_GO)]),
        ]));
        return children;
    }
    get nodes() {
        const nodes = (0, mithril_1.m)('div', { className: import_style_1.CSS.container }, [
            (0, mithril_1.m)('div', { className: import_style_1.CSS.title }, (0, mithril_1.m)('h6', (0, i18n_1.Translate)(i18n_1.T.COLLECTION_IMPORT_TITLE))),
        ]);
        // If the user is not the correct user type OR they don't have access to this feature, then block
        if (!this.canImport) {
            nodes.children.push((0, mithril_1.m)('p', { className: import_style_1.CSS.title }, (0, i18n_1.Translate)(i18n_1.T.COLLECTION_IMPORT_NOT_ALLOWED)));
            return nodes;
        }
        if (this.tabs.length > 0) {
            nodes.children.push((0, mithril_1.m)('div', { class: import_style_1.CSS.tabBar.container }, this.tabs.map((tab, i) => {
                return (0, mithril_1.m)('div', {
                    class: (0, classnames_1.classNames)(import_style_1.CSS.tabBar.tab, { [import_style_1.CSS.tabBar.tabActive]: this.activeTab === i }),
                    onclick: () => {
                        var _a, _b;
                        this.activeTab = i;
                        (_b = (_a = this.tabs[i]).onSelect) === null || _b === void 0 ? void 0 : _b.call(_a);
                    },
                }, tab.title);
            })));
        }
        switch (this.mode) {
            case 'EXCEL':
                nodes.children.push(...this.getExcelNodes());
                break;
            case 'CSV':
                nodes.children.push(...this.getCsvNodes());
                break;
            default:
        }
        return nodes;
    }
    viewTickOrNeeded(isComplete) {
        if (isComplete) {
            return icon_1.Icon.view(icons_1.MaterialIcon.CHECK, { className: import_style_1.CSS.isOptionIconComplete });
        }
        return icon_1.Icon.view(icons_1.MaterialIcon.KEY_RIGHT);
    }
    viewJobSelector() {
        const isComplete = this.tagName != null;
        let className = import_style_1.CSS.job.container + ' ' + import_style_1.CSS.option.container;
        if (isComplete) {
            className += ' ' + import_style_1.CSS.isOptionComplete;
        }
        return (0, mithril_1.m)('div', { className }, [
            (0, mithril_1.m)('div', { className: import_style_1.CSS.option.textContainer }, [
                this.viewTickOrNeeded(isComplete),
                (0, mithril_1.m)('span', { className: import_style_1.CSS.option.text }, (0, i18n_1.Translate)(i18n_1.T.COLLECTION_IMPORT_CHOOSE_JOB)),
            ]),
            (0, mithril_1.m)('div', { className: import_style_1.CSS.option.value }, [this.tag.view()]),
        ]);
    }
    viewExcelTemplateDownload() {
        return (0, mithril_1.m)('div', { className: (0, classnames_1.classNames)(import_style_1.CSS.option.container, { [import_style_1.CSS.option.downloadDisabled]: !this.form.value }) }, [
            (0, mithril_1.m)('div', { className: import_style_1.CSS.option.textContainer }, [
                (0, mithril_1.m)('span', { className: import_style_1.CSS.option.text }, (0, i18n_1.Translate)(i18n_1.T.COLLECTION_IMPORT_EXCEL_TEMPLATE)),
            ]),
            (0, mithril_1.m)('div', { className: import_style_1.CSS.option.value }, [
                (0, mithril_1.m)('button', {
                    className: import_style_1.CSS.option.download,
                    onclick: () => tslib_1.__awaiter(this, void 0, void 0, function* () {
                        if (this.form.value == null) {
                            return;
                        }
                        const result = yield services_1.Services.Department.rest.Collection.exportImportableExcelTemplate({
                            formVersion: this.form.value,
                        });
                        if (result == null) {
                            return;
                        }
                        if (result.error) {
                            notification_service_1.NotificationService.error({ key: export_notification_detail_ctrl_1.MessageReasonTranslations[result.error.reason] });
                        }
                        else if (result.url != null) {
                            services_1.Services.Download.download(result.url, result.fileName);
                        }
                    }),
                }, [icon_1.Icon.view(icons_1.MaterialIcon.FILE_DOWNLOAD), (0, i18n_1.Translate)(i18n_1.T.EXCEL)]),
            ]),
        ]);
    }
    viewUserGuideLink() {
        return (0, mithril_1.m)('div', { className: import_style_1.CSS.option.container }, [
            (0, mithril_1.m)('div', { className: import_style_1.CSS.option.textContainer }, [
                (0, i18n_1.Translate)(i18n_1.T.COLLECTION_IMPORT_EXCEL_USER_GUIDE),
                (0, mithril_1.m)('a', {
                    href: (0, i18n_1.Translate)(i18n_1.T.COLLECTION_IMPORT_EXCEL_USER_GUIDE_LINK),
                    target: '_blank',
                    style: `margin-left: ${theme_1.DefaultTheme.space(1)}`,
                }, (0, i18n_1.Translate)(i18n_1.T.HERE)),
            ]),
        ]);
    }
    viewFormSelector() {
        const isComplete = this.form.value != null;
        let className = import_style_1.CSS.form.container + ' ' + import_style_1.CSS.option.container;
        if (isComplete) {
            className += ' ' + import_style_1.CSS.isOptionComplete;
        }
        return (0, mithril_1.m)('div', { className }, [
            (0, mithril_1.m)('div', { className: import_style_1.CSS.option.textContainer }, [
                this.viewTickOrNeeded(isComplete),
                (0, mithril_1.m)('span', { className: import_style_1.CSS.option.text }, (0, i18n_1.Translate)(i18n_1.T.COLLECTION_IMPORT_CHOOSE_FORM)),
            ]),
            (0, mithril_1.m)('div', { className: import_style_1.CSS.option.value }, [input_select_1.SelectInputComponent.view(this.form)]),
        ]);
    }
    viewFileUpload() {
        const isComplete = this.fileUpload.isFilePresent;
        let className = import_style_1.CSS.file.container + ' ' + import_style_1.CSS.option.container;
        if (isComplete) {
            className += ' ' + import_style_1.CSS.isOptionComplete;
            return (0, mithril_1.m)('div', { className }, [
                (0, mithril_1.m)('div', { className: import_style_1.CSS.option.textContainer }, [
                    this.viewTickOrNeeded(isComplete),
                    (0, mithril_1.m)('span', { className: import_style_1.CSS.option.text }, this.mode === 'EXCEL' ? (0, i18n_1.Translate)(i18n_1.T.COLLECTION_IMPORT_FILE_EXCEL) : (0, i18n_1.Translate)(i18n_1.T.COLLECTION_IMPORT_FILE_CSV)),
                ]),
                (0, mithril_1.m)('div', { className: import_style_1.CSS.option.value }, [
                    (0, mithril_1.m)('div', this.fileUpload.file.name),
                    this.isImporting ? null : image_button_1.ImageButton.view(icons_1.MaterialIcon.CLOSE, { onclick: this.fileUpload.reset }),
                ]),
            ]);
        }
        return (0, mithril_1.m)('div', { className }, [
            (0, mithril_1.m)('div', { className: import_style_1.CSS.option.textContainer }, [
                this.viewTickOrNeeded(isComplete),
                (0, mithril_1.m)('span', { className: import_style_1.CSS.option.text }, (0, i18n_1.Translate)(i18n_1.T.COLLECTION_IMPORT_CHOOSE_FILE_TYPE, (0, i18n_1.Translate)(this.mode === 'EXCEL' ? i18n_1.T.EXCEL : i18n_1.T.CSV))),
            ]),
            (0, mithril_1.m)('div', { className: import_style_1.CSS.file.drop }, this.fileUpload.view()),
        ]);
    }
    viewFileMappings() {
        const collectionCount = this.csvImport.collections.length;
        const tooManyCollections = (0, mithril_1.m)('div', { className: import_style_1.CSS.isTooManyCollections }, (0, i18n_1.Translate)(i18n_1.T.COLLECTION_IMPORT_TOO_MANY, { count: collectionCount, max: MAX_COLLECTIONS }));
        return (0, mithril_1.m)('div', { className: import_style_1.CSS.csv.container }, [
            (0, mithril_1.m)('div', { className: import_style_1.CSS.option.textContainer }, [
                this.viewTickOrNeeded(this.isValid),
                (0, mithril_1.m)('span', { className: import_style_1.CSS.option.text }, (0, i18n_1.Translate)(i18n_1.T.COLLECTION_IMPORT_MAP_FIELDS)),
            ]),
            collectionCount > MAX_COLLECTIONS ? tooManyCollections : null,
            this.csvImport.view(),
        ]);
    }
}
exports.CSVFileImportPage = CSVFileImportPage;
tslib_1.__decorate([
    mobx_1.observable
], CSVFileImportPage.prototype, "isImporting", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CSVFileImportPage.prototype, "progressCurrent", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CSVFileImportPage.prototype, "progressTotal", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CSVFileImportPage.prototype, "mode", void 0);
tslib_1.__decorate([
    mobx_1.computed
], CSVFileImportPage.prototype, "isValid", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CSVFileImportPage.prototype, "processCSVFile", null);
tslib_1.__decorate([
    mobx_1.action
], CSVFileImportPage.prototype, "setProgress", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CSVFileImportPage.prototype, "processFileFailure", null);
tslib_1.__decorate([
    mobx_1.action
], CSVFileImportPage.prototype, "setImporting", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CSVFileImportPage.prototype, "importCSVCollections", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CSVFileImportPage.prototype, "importExcelCollections", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CSVFileImportPage.prototype, "processFormList", null);
function renderImportResults(jobID, results) {
    return (0, mithril_1.m)('div', { className: import_style_1.CSS.importNotifications.container }, [
        results.meta.length ? renderNotifications(results.meta) : null,
        ...results.collections.map((result) => renderCollectionResult(jobID, result)),
    ]);
}
function renderCollectionResult(jobID, result) {
    var _a, _b;
    return (0, mithril_1.m)('div', [
        (0, mithril_1.m)('div', [
            importResultIcon(result.status),
            ((_a = result.collection) === null || _a === void 0 ? void 0 : _a.createdAt)
                ? (0, mithril_1.m)('a', {
                    className: import_style_1.CSS.importNotifications.collectionLink,
                    href: services_1.Services.Route.getRouteURL(page_1.IkePageName.map, {
                        t: jobID,
                        collection: (_b = result.collection) === null || _b === void 0 ? void 0 : _b.uuid,
                    }),
                }, result.title)
                : result.title,
            `(${translateStatus(result.status)})`,
        ]),
        renderNotifications(result.notifications),
    ]);
}
function importResultIcon(status) {
    switch (status) {
        case 'created':
            return icon_1.Icon.view(icons_1.MaterialIcon.ADD);
        case 'updated':
            return icon_1.Icon.view(icons_1.MaterialIcon.EDIT);
        case 'error':
            return icon_1.Icon.view(icons_1.MaterialIcon.WARNING);
        default:
            return null;
    }
}
function renderNotifications(notifications) {
    if (notifications.length === 0) {
        return null;
    }
    return (0, mithril_1.m)('table', [
        (0, mithril_1.m)('tr', [
            (0, mithril_1.m)('th', 'Sheet'),
            (0, mithril_1.m)('th', 'Field'),
            (0, mithril_1.m)('th', 'Cell'),
            // m('th', 'Severity'),
            (0, mithril_1.m)('th', 'Reason'),
        ]),
        ...notifications.map((n) => (0, mithril_1.m)('tr', [
            (0, mithril_1.m)('td', n.sheetName),
            (0, mithril_1.m)('td', n.fieldName),
            (0, mithril_1.m)('td', n.cell),
            // m('td', n.level),
            (0, mithril_1.m)('td', (0, i18n_1.Translate)(`REASON_EXPORT_IMPORTABLE_EXCEL_${n.reason.toUpperCase()}`)),
        ])),
    ]);
}
function translateStatus(status) {
    switch (status) {
        case 'created':
            return (0, i18n_1.Translate)(i18n_1.T.CREATED);
        case 'updated':
            return (0, i18n_1.Translate)(i18n_1.T.UPDATED);
        case 'error':
            return (0, i18n_1.Translate)(i18n_1.T.IMPORT_FAILED);
        default:
            return '';
    }
}
//# sourceMappingURL=import.page.js.map